import { FormikValues } from 'formik';
import { useCallback, useEffect, useState } from 'react';

import { useVerificationCodeByPhone } from '@api/auth';

import Form from '@controls/Form';

import { CodeInput } from '@components/CodeInput';
import FormMessage from '@components/controls/Form/Message';
import Button from '@components/uiKit/Button';
import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';

import { DEFAULT_TIMEZONE } from '@scripts/constants';
import { CustomersTypeEnum } from '@scripts/enums';

import { FormSubmitBinder } from './FormSubmitBinder';
import styles from './SendCode.module.scss';
import { FieldType, SendCodeByPhoneProps } from './types';
import { formatPhoneNumberWithMask } from './utils';

const SendCode = ({
    logIn,
    backToSignin,
    phone,
    setPhone,
    email = '',
    errorMessage,
    registerInfo,
    setErrorMessage,
    isLogin,
    register,
    sendCodeByEmailForRegistration,
    startTimer,
    stopTimer,
    timeLeft,
    isCanSendAgain,
    hidePrimaryActions = false,
    bindSubmit,
}: SendCodeByPhoneProps) => {
    const { mutateAsync: verificationCodeByPhone } = useVerificationCodeByPhone();

    const [isLoading, setLoading] = useState(false);
    const [code, setCode] = useState('');

    const loginByPhone = async () => {
        await logIn({ phone: phone || '', code: code });
    };

    const handlerSubmit = async () => {
        try {
            await verificationCodeByPhone({ phone: phone || '' });
        } catch (error: any) {
            setErrorMessage(error.message);
        }

        setErrorMessage('');
        setCode('');
        startTimer?.();
    };

    const handleSwitchToEmailLogin = useCallback(() => {
        setErrorMessage('');

        if (isLogin) {
            backToSignin();
        } else {
            sendCodeByEmailForRegistration?.(email);
        }
    }, [isLogin, backToSignin, setErrorMessage, sendCodeByEmailForRegistration, email]);

    useEffect(() => {
        if (code && code.length === 4 && isLogin) {
            setLoading(prev => !prev);
            loginByPhone()
                .catch((error: any) => {
                    setErrorMessage(error.message);
                })
                .finally(() => {
                    setLoading(false);
                    stopTimer?.();
                });
        }

        if (code && code.length === 4 && registerInfo && registerInfo.phone && !isLogin) {
            setLoading(prev => !prev);
            try {
                register({
                    ...(registerInfo.inn && {
                        ...registerInfo.inn,
                        inn: registerInfo.inn.value,
                    }),
                    type:
                        registerInfo?.inn?.value?.length === 10
                            ? CustomersTypeEnum.ENTITY
                            : registerInfo?.inn?.value?.length === 12
                              ? CustomersTypeEnum.IP
                              : CustomersTypeEnum.PHYSICAL,
                    code: code,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE,
                    phone: phone,
                    email: email,
                });
            } catch (error: any) {
                setErrorMessage(error.message);
            } finally {
                setLoading(false);
                stopTimer?.();
            }
        }
    }, [code, isLogin, registerInfo, phone, email, register, stopTimer]);

    return (
        <Form onSubmit={handlerSubmit} initialValues={{ code: '' }}>
            {() => (
                <div className={styles.root}>
                    {bindSubmit && <FormSubmitBinder onBind={bindSubmit} />}
                    <div className={styles.hint}>
                        Отправили на номер{' '}
                        <span className={styles.contact}>
                            {formatPhoneNumberWithMask(phone as string, FieldType.PHONE)}
                        </span>
                        <span
                            onClick={() => {
                                backToSignin();
                                setErrorMessage('');
                                setPhone('');
                            }}
                            className={styles.change}
                        >
                            Изменить
                        </span>
                    </div>
                    <div className={styles.code}>
                        {isLoading ? <LoadingSpinner embedded noPadding /> : <CodeInput onChange={setCode} />}
                    </div>
                    {errorMessage && <FormMessage type="error" message={errorMessage} className={styles.error} />}
                    {(timeLeft ?? 0) > 0 && !isCanSendAgain && (
                        <div className={styles.timer}>
                            <div className={styles.timerText}>
                                Если код не придёт, можно получить новый через {timeLeft ?? 0} сек. или{' '}
                                {isLogin ? ' войти ' : ' подтвердить '}
                                <span onClick={handleSwitchToEmailLogin} className={styles.link}>
                                    по email
                                </span>
                            </div>
                        </div>
                    )}
                    {((timeLeft ?? 0) === 0 || isCanSendAgain) && (
                        <div>
                            {!errorMessage && (
                                <div className={styles.hintBox}>
                                    Не пришел код? Запросите новый или {isLogin ? 'войдите ' : 'подтвердите '} по email
                                </div>
                            )}
                            {!hidePrimaryActions && (
                                <Button
                                    type="submit"
                                    variant="mainOrange"
                                    size="lg"
                                    fullWidth
                                    data-testid="button-submit"
                                    role="button"
                                    className={styles.resend}
                                >
                                    Получить новый код
                                </Button>
                            )}
                            {!hidePrimaryActions && (
                                <Button
                                    type="button"
                                    variant="reserveWhite"
                                    size="lg"
                                    fullWidth
                                    onClick={handleSwitchToEmailLogin}
                                    className={styles.altButton}
                                >
                                    {isLogin ? 'Войти по email' : 'Подтвердить по email'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Form>
    );
};

export default SendCode;
